import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faSearch,
  faSlidersH,
  faUndoAlt,
} from '@fortawesome/free-solid-svg-icons';
import { ErrorPage } from '@edx/frontend-platform/react';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import { DEFAULT_PAGE_INDEX } from '@src/data/course-list-search/constants';
import { useCourseListSearch } from '@src/data/course-list-search/hooks';
import type { DataTableFilter } from '@src/data/course-list-search/types';
import { AlertNotification, Head, Loading } from '@src/generic';
import HomeCourseCardSlot from '@src/plugin-slots/HomeCourseCardSlot';
import { ROUTES } from '@src/routes';
import messages from './messages';

const PAGE_SIZE = 12;

const getLanguageName = (value: string, locale: string) => {
  try {
    const languageNames = new Intl.DisplayNames([locale, 'fr'], { type: 'language' });
    return languageNames.of(value) || value.toUpperCase();
  } catch (error) {
    return value.toUpperCase();
  }
};

const buildFilterPayload = (organizations: string[], languages: string[]): DataTableFilter[] => {
  const filters: DataTableFilter[] = [];

  if (organizations.length) {
    filters.push({ id: 'org', value: organizations });
  }

  if (languages.length) {
    filters.push({ id: 'language', value: languages });
  }

  return filters;
};

const CatalogPage = () => {
  const intl = useIntl();
  const config = getConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('search_query') || '';
  const selectedOrganizations = searchParams.getAll('org');
  const selectedLanguages = searchParams.getAll('language');
  const pageIndex = Math.max(Number(searchParams.get('page') || DEFAULT_PAGE_INDEX), DEFAULT_PAGE_INDEX);
  const [searchInput, setSearchInput] = useState(searchQuery);

  const {
    data: courseData,
    isLoading,
    isError,
    isFetching,
    fetchData,
  } = useCourseListSearch({
    pageSize: PAGE_SIZE,
    pageIndex,
    searchString: searchQuery,
    filters: {
      ...(selectedOrganizations.length ? { org: selectedOrganizations } : {}),
      ...(selectedLanguages.length ? { language: selectedLanguages } : {}),
    },
    enableCourseSortingByStartDate: config.ENABLE_COURSE_SORTING_BY_START_DATE || false,
  });

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    fetchData({
      pageSize: PAGE_SIZE,
      pageIndex,
      searchString: searchQuery,
      filters: buildFilterPayload(selectedOrganizations, selectedLanguages),
    });
  }, [
    fetchData,
    pageIndex,
    searchQuery,
    selectedLanguages.join('|'),
    selectedOrganizations.join('|'),
  ]);

  const updateSearchParams = (mutate: (params: URLSearchParams) => void) => {
    const nextParams = new URLSearchParams(searchParams);
    mutate(nextParams);
    setSearchParams(nextParams);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    updateSearchParams((nextParams) => {
      const trimmedQuery = searchInput.trim();

      if (trimmedQuery) {
        nextParams.set('search_query', trimmedQuery);
      } else {
        nextParams.delete('search_query');
      }

      nextParams.delete('page');
    });
  };

  const handleToggleFilter = (key: 'org' | 'language', value: string) => {
    updateSearchParams((nextParams) => {
      const values = new Set(nextParams.getAll(key));

      if (values.has(value)) {
        values.delete(value);
      } else {
        values.add(value);
      }

      nextParams.delete(key);
      [...values].forEach((entry) => nextParams.append(key, entry));
      nextParams.delete('page');
    });
  };

  const handleResetAll = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };

  const handlePageChange = (nextPage: number) => {
    updateSearchParams((nextParams) => {
      if (nextPage <= DEFAULT_PAGE_INDEX) {
        nextParams.delete('page');
      } else {
        nextParams.set('page', String(nextPage));
      }
    });
  };

  const organizations = useMemo(
    () => Object.entries(courseData?.aggs?.org?.terms || {}).map(([value, count]) => ({
      value,
      label: value,
      count,
    })),
    [courseData?.aggs?.org?.terms],
  );

  const languages = useMemo(
    () => Object.entries(courseData?.aggs?.language?.terms || {}).map(([value, count]) => ({
      value,
      label: getLanguageName(value, intl.locale),
      count,
    })),
    [courseData?.aggs?.language?.terms, intl.locale],
  );

  const organizationLookup = useMemo(
    () => new Map(organizations.map((organization) => [organization.value, organization.label])),
    [organizations],
  );
  const languageLookup = useMemo(
    () => new Map(languages.map((language) => [language.value, language.label])),
    [languages],
  );

  const activeFilterTags = useMemo(() => {
    const tags: string[] = [];

    if (searchQuery) {
      tags.push(intl.formatMessage(messages.activeSearchTag, { query: searchQuery }));
    }

    selectedOrganizations.forEach((organization) => {
      tags.push(organizationLookup.get(organization) || organization);
    });

    selectedLanguages.forEach((language) => {
      tags.push(languageLookup.get(language) || getLanguageName(language, intl.locale));
    });

    return tags;
  }, [
    intl.locale,
    languageLookup,
    organizationLookup,
    searchQuery,
    selectedLanguages,
    selectedOrganizations,
  ]);

  const totalCourses = courseData?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCourses / PAGE_SIZE));
  const hasResults = (courseData?.results?.length ?? 0) > 0;
  const hasActiveFilters = Boolean(searchQuery || selectedOrganizations.length || selectedLanguages.length);
  const firstResultIndex = hasResults ? (pageIndex * PAGE_SIZE) + 1 : 0;
  const lastResultIndex = hasResults ? firstResultIndex + (courseData?.results?.length ?? 0) - 1 : 0;
  const pageTitle = searchQuery
    ? intl.formatMessage(messages.resultsTitle, { query: searchQuery })
    : intl.formatMessage(messages.pageTitle);

  if (isLoading && !courseData) {
    return <Loading />;
  }

  return (
    <>
      <Head title={pageTitle} />
      <div className="catalog-browse-page">
        <section className="catalog-browse-hero">
          <div className="catalog-browse-hero__container">
            <Link className="catalog-browse-hero__back-link" to={ROUTES.HOME}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {intl.formatMessage(messages.backToHome)}
            </Link>

            <div className="catalog-browse-hero__header">
              <div className="catalog-browse-hero__copy">
                <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.eyebrow)}</span>
                <h1 className="catalog-browse-hero__title">{pageTitle}</h1>
                <p className="catalog-browse-hero__description">
                  {intl.formatMessage(messages.heroDescription)}
                </p>
              </div>

              <div className="catalog-browse-hero__status-card">
                <span className="catalog-browse-hero__status-value">{totalCourses}</span>
                <span className="catalog-browse-hero__status-label">
                  {intl.formatMessage(messages.availableCoursesLabel)}
                </span>
                <p>
                  {intl.formatMessage(messages.heroStatusDescription)}
                </p>
              </div>
            </div>

            <form className="catalog-browse-search" onSubmit={handleSearchSubmit} role="search">
              <label className="catalog-browse-search__field">
                <FontAwesomeIcon icon={faSearch} />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder={intl.formatMessage(messages.searchPlaceholder)}
                  aria-label={intl.formatMessage(messages.searchInputAriaLabel)}
                />
              </label>
              <button type="submit" className="catalog-ui-button catalog-ui-button--primary">
                {intl.formatMessage(messages.searchButton)}
              </button>
            </form>
          </div>
        </section>

        <section className="catalog-browse-content">
          <div className="catalog-browse-content__container">
            <aside className="catalog-browse-sidebar">
              <div className="catalog-browse-sidebar__card">
                <div className="catalog-browse-sidebar__header">
                  <div>
                    <span className="catalog-browse-sidebar__eyebrow">{intl.formatMessage(messages.filtersEyebrow)}</span>
                    <h2 className="catalog-browse-sidebar__title">
                      <FontAwesomeIcon icon={faSlidersH} />
                      {intl.formatMessage(messages.filtersTitle)}
                    </h2>
                    <p className="catalog-browse-sidebar__summary">
                      {intl.formatMessage(messages.filtersSummary)}
                    </p>
                  </div>
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      className="catalog-browse-sidebar__reset"
                      onClick={handleResetAll}
                    >
                      <FontAwesomeIcon icon={faUndoAlt} />
                      {intl.formatMessage(messages.resetFilters)}
                    </button>
                  ) : null}
                </div>

                {organizations.length ? (
                  <div className="catalog-browse-filter-group">
                    <h3>{intl.formatMessage(messages.organizationsTitle)}</h3>
                    <div className="catalog-browse-chip-group">
                      {organizations.map((organization) => {
                        const isActive = selectedOrganizations.includes(organization.value);

                        return (
                          <button
                            key={organization.value}
                            type="button"
                            className={`catalog-browse-chip${isActive ? ' catalog-browse-chip--active' : ''}`}
                            onClick={() => handleToggleFilter('org', organization.value)}
                          >
                            <span>{organization.label}</span>
                            <span>{organization.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {languages.length ? (
                  <div className="catalog-browse-filter-group">
                    <h3>{intl.formatMessage(messages.languagesTitle)}</h3>
                    <div className="catalog-browse-chip-group">
                      {languages.map((language) => {
                        const isActive = selectedLanguages.includes(language.value);

                        return (
                          <button
                            key={language.value}
                            type="button"
                            className={`catalog-browse-chip${isActive ? ' catalog-browse-chip--active' : ''}`}
                            onClick={() => handleToggleFilter('language', language.value)}
                          >
                            <span>{language.label}</span>
                            <span>{language.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </aside>

            <div className="catalog-browse-results">
              <div className="catalog-browse-results__header">
                <div className="catalog-browse-results__copy">
                  <span className="catalog-home-section__eyebrow">
                    {searchQuery
                      ? intl.formatMessage(messages.resultsEyebrowSearch)
                      : intl.formatMessage(messages.resultsEyebrowDefault)}
                  </span>
                  <h2>
                    {searchQuery
                      ? intl.formatMessage(messages.resultsHeadingSearch)
                      : intl.formatMessage(messages.resultsHeadingDefault)}
                  </h2>
                  <p>
                    {hasResults
                      ? intl.formatMessage(messages.resultsSummary, {
                        first: firstResultIndex,
                        last: lastResultIndex,
                        total: totalCourses,
                      })
                      : intl.formatMessage(messages.noSelectionMatch)}
                  </p>
                </div>

                <div className="catalog-browse-results__meta">
                  <span className="catalog-browse-results__status">
                    {isFetching
                      ? intl.formatMessage(messages.refreshingStatus)
                      : intl.formatMessage(messages.resultsCountStatus, { total: totalCourses })}
                  </span>
                </div>
              </div>

              {activeFilterTags.length ? (
                <div className="catalog-browse-active-filters">
                  {activeFilterTags.map((tag) => (
                    <span key={tag} className="catalog-browse-active-filters__tag">{tag}</span>
                  ))}
                </div>
              ) : null}

              {isError ? (
                <div className="catalog-home-message-shell">
                  <AlertNotification
                    className="my-0"
                    variant="danger"
                    title={intl.formatMessage(messages.errorTitle)}
                    message={(
                      <ErrorPage
                        message={intl.formatMessage(messages.errorMessage, {
                          supportEmail: config.INFO_EMAIL,
                        })}
                      />
                    )}
                  />
                </div>
              ) : null}

              {!isError && !hasResults ? (
                <div className="catalog-browse-empty-state">
                  <h3>{intl.formatMessage(messages.emptyStateTitle)}</h3>
                  <p>
                    {intl.formatMessage(messages.emptyStateDescription)}
                  </p>
                  {hasActiveFilters ? (
                    <button type="button" className="catalog-ui-button catalog-ui-button--secondary" onClick={handleResetAll}>
                      {intl.formatMessage(messages.resetSearch)}
                    </button>
                  ) : null}
                </div>
              ) : null}

              {!isError && hasResults ? (
                <>
                  <div className="catalog-browse-grid">
                    {courseData?.results.map((course) => (
                      <HomeCourseCardSlot key={course.id} original={course} />
                    ))}
                  </div>

                  {pageCount > 1 ? (
                    <div className="catalog-browse-pagination">
                      <button
                        type="button"
                        className="catalog-ui-button catalog-ui-button--secondary"
                        disabled={pageIndex <= DEFAULT_PAGE_INDEX}
                        onClick={() => handlePageChange(pageIndex - 1)}
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        {intl.formatMessage(messages.previousPage)}
                      </button>

                      <span className="catalog-browse-pagination__summary">
                        {intl.formatMessage(messages.paginationSummary, {
                          current: pageIndex + 1,
                          total: pageCount,
                        })}
                      </span>

                      <button
                        type="button"
                        className="catalog-ui-button catalog-ui-button--secondary"
                        disabled={pageIndex + 1 >= pageCount}
                        onClick={() => handlePageChange(pageIndex + 1)}
                      >
                        {intl.formatMessage(messages.nextPage)}
                        <FontAwesomeIcon icon={faArrowRight} />
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CatalogPage;
